import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CheckCircle2, Circle, Edit, Plus, Trash2, ChevronUp, ChevronDown, Undo } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get user ID from localStorage
const getUserId = () => {
  let userId = localStorage.getItem('timetactix_user_id');
  if (!userId) {
    userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('timetactix_user_id', userId);
  }
  return userId;
};

// Analytics helper
const trackEvent = (eventName, properties = {}) => {
  // Stub for analytics
  console.log('Analytics event:', eventName, properties);
};

const EditableTaskList = ({ 
  tasks, 
  onTasksUpdate, 
  currentTaskId = null, 
  onCurrentTaskChange = null, 
  sprintId = null,
  isTimerRunning = false,
  onTaskComplete = null,
  onTaskUndo = null 
}) => {
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [draftTitles, setDraftTitles] = useState({}); // Draft titles keyed by task.id
  const [isEditingFlags, setIsEditingFlags] = useState({}); // Editing flags keyed by task.id
  const inputRef = useRef(null);

  // Auto-save tasks to backend
  const autosaveTasks = async (updatedTasks) => {
    if (!sprintId) return;
    
    try {
      const token = localStorage.getItem('authToken');
      const userId = getUserId();
      
      await axios.put(`${API}/sprints/${sprintId}/tasks?user_id=${userId}`, {
        tasks: updatedTasks.map((task, index) => ({
          id: task.id,
          title: task.title,
          description: task.description || '',
          estimated_minutes: task.estimated_minutes,
          status: task.status,
          order_index: index
        })),
        user_id: userId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to autosave tasks:', error);
    }
  };

  // Handle task reordering with up/down arrows
  const moveTask = (taskIndex, direction) => {
    const newTasks = [...tasks];
    const newIndex = direction === 'up' ? taskIndex - 1 : taskIndex + 1;
    
    // Check bounds
    if (newIndex < 0 || newIndex >= newTasks.length) return;
    
    // Swap tasks
    [newTasks[taskIndex], newTasks[newIndex]] = [newTasks[newIndex], newTasks[taskIndex]];
    
    onTasksUpdate(newTasks);
    autosaveTasks(newTasks);

    trackEvent('subtask_reordered', {
      subtask_id: tasks[taskIndex].id,
      sprint_id: sprintId,
      from_index: taskIndex,
      to_index: newIndex,
      timestamp: new Date().toISOString()
    });
  };

  // Start editing a task
  const startEditing = (taskId, field, currentValue) => {
    setEditingTaskId(taskId);
    setIsEditingFlags(prev => ({ ...prev, [taskId]: true }));
    setDraftTitles(prev => ({ ...prev, [taskId]: currentValue }));
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 10);
  };

  // Handle title change during editing
  const handleTitleChange = (taskId, newValue) => {
    setDraftTitles(prev => ({ ...prev, [taskId]: newValue }));
    
    // Determine which page we're on
    const page = isTimerRunning ? "timer" : "prep";
    const task = tasks.find(t => t.id === taskId);
    const externalTitle = task?.title || '';
    const isEditing = isEditingFlags[taskId] || false;
    const draftTitle = draftTitles[taskId];
    
    console.log("TaskTitle Edit State", {
      page,
      taskId,
      isEditing,
      draftTitle: newValue,
      externalTitle,
      syncingSkipped: true // We're in onChange, not syncing
    });
  };

  // Get input value for task title
  const getTaskTitleValue = (task) => {
    const isEditing = isEditingFlags[task.id];
    const hasDraft = draftTitles[task.id] !== undefined;
    
    // Only hydrate from external when not editing and no draft exists
    if (!isEditing && !hasDraft) {
      return task.title;
    }
    
    // Return draft (even if empty string)
    return draftTitles[task.id] || '';
  };

  // Update task field
  const updateTaskField = (taskId, field, value) => {
    const updatedTasks = tasks.map(task => 
      task.id === taskId 
        ? { ...task, [field]: field === 'estimated_minutes' ? parseInt(value) || 1 : value }
        : task
    );
    onTasksUpdate(updatedTasks);
    autosaveTasks(updatedTasks);

    trackEvent('subtask_edited', {
      subtask_id: taskId,
      sprint_id: sprintId,
      field,
      timestamp: new Date().toISOString()
    });
  };

  // Save editing changes
  const saveEdit = () => {
    if (editingTaskId && draftTitles[editingTaskId] !== undefined) {
      const draftTitle = draftTitles[editingTaskId].trim();
      if (draftTitle !== '') {
        updateTaskField(editingTaskId, 'title', draftTitle);
      }
    }
    setEditingTaskId(null);
    setIsEditingFlags(prev => ({ ...prev, [editingTaskId]: false }));
    setDraftTitles(prev => ({ ...prev, [editingTaskId]: undefined }));
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingTaskId(null);
    setIsEditingFlags(prev => ({ ...prev, [editingTaskId]: false }));
    setDraftTitles(prev => ({ ...prev, [editingTaskId]: undefined }));
  };

  // Handle key presses during editing
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  // Add new task
  const addNewTask = () => {
    const newTask = {
      id: `task-${Date.now()}`,
      title: 'New Task',
      description: '',
      estimated_minutes: 15,
      status: 'pending'
    };
    
    const updatedTasks = [...tasks, newTask];
    onTasksUpdate(updatedTasks);
    autosaveTasks(updatedTasks);
    
    setTimeout(() => startEditing(newTask.id, 'title', 'New Task'), 100);

    trackEvent('subtask_added', {
      subtask_id: newTask.id,
      sprint_id: sprintId,
      timestamp: new Date().toISOString()
    });
  };

  // Delete task
  const deleteTask = (taskId) => {
    const updatedTasks = tasks.filter(task => task.id !== taskId);
    onTasksUpdate(updatedTasks);
    autosaveTasks(updatedTasks);

    if (currentTaskId === taskId && onCurrentTaskChange && isTimerRunning) {
      const nextIncompleteTask = updatedTasks.find(task => task.status !== 'completed');
      if (nextIncompleteTask) {
        onCurrentTaskChange(nextIncompleteTask.id);
      }
    }

    trackEvent('subtask_deleted', {
      subtask_id: taskId,
      sprint_id: sprintId,
      timestamp: new Date().toISOString()
    });
  };

  // Update estimated minutes
  const updateMinutes = (taskId, delta) => {
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      const newMinutes = Math.max(1, Math.min(180, task.estimated_minutes + delta));
      updateTaskField(taskId, 'estimated_minutes', newMinutes);
    }
  };

  // Handle task completion
  const handleTaskComplete = (taskId) => {
    if (onTaskComplete) {
      onTaskComplete(taskId);
      trackEvent('subtask_completed', {
        subtask_id: taskId,
        sprint_id: sprintId,
        timestamp: new Date().toISOString()
      });
    } else {
      // Fallback for components that don't provide onTaskComplete
      updateTaskField(taskId, 'status', 'completed');
    }
  };

  // Handle task undo
  const handleTaskUndo = (taskId) => {
    if (onTaskUndo) {
      onTaskUndo(taskId);
      trackEvent('subtask_undone', {
        subtask_id: taskId,
        sprint_id: sprintId,
        timestamp: new Date().toISOString()
      });
    } else {
      // Fallback for components that don't provide onTaskUndo
      updateTaskField(taskId, 'status', 'pending');
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {tasks.map((task, index) => {
          const taskId = String(task.id);
          return (
            <div
              key={taskId}
              className={`group p-3 bg-slate-700/50 rounded-lg border transition-all cursor-pointer task-row ${
                currentTaskId === task.id 
                  ? 'border-amber-500 bg-amber-500/10' 
                  : 'border-slate-600 hover:border-slate-500'
              }`}
              onClick={() => {
                if (onCurrentTaskChange && task.status !== 'completed') {
                  onCurrentTaskChange(task.id);
                  trackEvent('subtask_selected', {
                    subtask_id: task.id,
                    sprint_id: sprintId,
                    timestamp: new Date().toISOString()
                  });
                }
              }}
            >
              {/* Main task content row */}
              <div className="flex items-start space-x-3">
                {/* Task Status Icon */}
                <div className="flex-shrink-0 mt-1">
                  {task.status === 'completed' ? (
                    <CheckCircle2 className="h-5 w-5 text-green-400" />
                  ) : currentTaskId === task.id ? (
                    <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                    </div>
                  ) : (
                    <Circle className="h-5 w-5 text-slate-400 hover:text-slate-300 transition-colors" />
                  )}
                </div>

                {/* Task Content */}
                <div className="flex-grow min-w-0">
                  {/* Task Title with Edit Icon */}
                  <div className="flex items-center space-x-2">
                    {editingTaskId === task.id ? (
                      <Input
                        ref={inputRef}
                        value={getTaskTitleValue(task)}
                        onChange={(e) => handleTitleChange(task.id, e.target.value)}
                        onFocus={() => setIsEditingFlags(prev => ({ ...prev, [task.id]: true }))}
                        onBlur={() => {
                          setIsEditingFlags(prev => ({ ...prev, [task.id]: false }));
                          saveEdit();
                        }}
                        onKeyDown={handleKeyPress}
                        className="bg-slate-600 border-slate-500 text-white h-8 text-sm flex-grow"
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <>
                        <div
                          className={`font-medium flex-grow ${
                            task.status === 'completed' ? 'text-green-400 line-through' : 'text-white'
                          }`}
                        >
                          {task.title}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditing(task.id, 'title', task.title);
                          }}
                          className="p-1 text-slate-500 hover:text-slate-300 transition-all duration-200 opacity-0 group-hover:opacity-100 task-action-button"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>

                  {/* Controls positioned under task description - Left side */}
                  <div className="mt-2 flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                    {/* Time adjustment controls */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateMinutes(task.id, -5);
                        }}
                        className="w-6 h-6 rounded flex items-center justify-center text-slate-500 hover:text-slate-300 hover:bg-slate-600 transition-all duration-200 opacity-0 group-hover:opacity-100 task-action-button"
                      >
                        −
                      </button>
                      <span className="text-sm text-slate-400 min-w-[60px] text-center">
                        {task.estimated_minutes} min
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateMinutes(task.id, 5);
                        }}
                        className="w-6 h-6 rounded flex items-center justify-center text-slate-500 hover:text-slate-300 hover:bg-slate-600 transition-all duration-200 opacity-0 group-hover:opacity-100 task-action-button"
                      >
                        +
                      </button>
                    </div>

                    {/* Reordering arrows */}
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveTask(index, 'up');
                        }}
                        disabled={index === 0}
                        className={`w-8 h-8 rounded flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100 task-action-button ${
                          index === 0 
                            ? 'text-slate-600 cursor-not-allowed' 
                            : 'text-slate-500 hover:text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        <ChevronUp className="h-5 w-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveTask(index, 'down');
                        }}
                        disabled={index === tasks.length - 1}
                        className={`w-8 h-8 rounded flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100 task-action-button ${
                          index === tasks.length - 1 
                            ? 'text-slate-600 cursor-not-allowed' 
                            : 'text-slate-500 hover:text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        <ChevronDown className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Action buttons - Right side */}
                <div className="flex-shrink-0 flex flex-col space-y-1">
                  {/* Task completion/undo button */}
                  {task.status === 'completed' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTaskUndo(task.id);
                      }}
                      className="px-3 py-1 rounded text-xs bg-slate-600 text-slate-300 hover:bg-slate-500 hover:text-white transition-all duration-200 opacity-0 group-hover:opacity-100 task-action-button"
                    >
                      <Undo className="h-3 w-3 mr-1 inline" />
                      Undo
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTaskComplete(task.id);
                      }}
                      className="px-3 py-1 rounded text-xs bg-green-600 text-white hover:bg-green-500 transition-all duration-200 opacity-0 group-hover:opacity-100 task-action-button"
                    >
                      Complete
                    </button>
                  )}
                  
                  {/* Delete button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteTask(task.id);
                    }}
                    className="p-1 text-slate-500 hover:text-red-400 transition-all duration-200 opacity-0 group-hover:opacity-100 task-action-button"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add New Task Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={addNewTask}
        className="w-full text-slate-400 hover:text-slate-300 border-2 border-dashed border-slate-600 hover:border-slate-500 hover:bg-slate-700/30 h-12"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Task
      </Button>
    </div>
  );
};

export default EditableTaskList;